/**
 * 全屏编辑器的新手引导：贴在真实元素上的分步提示（antd Tour），不是弹窗。
 *
 * 弹窗只能「讲」，用户关掉后要自己去找按钮在哪；贴在元素上的提示是「指」，
 * 看完手就在那个位置上了。每一步的目标元素用 data-tour 标记，找不到的那步
 * 退化成居中卡片，不会因为某个面板没渲染就整段卡死。
 */
import { Tour, type TourProps } from 'antd';
import { useEffect, useState } from 'react';

const SEEN_KEY = 'pp_editor_tour_v2';

const at = (name: string) => () => document.querySelector<HTMLElement>(`[data-tour="${name}"]`) ?? document.body;

const STEPS: TourProps['steps'] = [
  {
    title: '把表里的字段印上去',
    description: '点一下字段名就落到画布上。关联字段会直接生成一张明细表，子表有几行就印几行。',
    target: at('fields'),
    placement: 'right',
  },
  {
    title: '加区块',
    description: '标题、表格、分栏、签章……点一下就加到末尾。在画布文字里敲 / 也能唤出同一份菜单。',
    target: at('blocks'),
    placement: 'right',
  },
  {
    title: '画布上直接改',
    description: '双击改字；表格里单击选格、Shift 拉选区、右键合并或增删行列；拖列线调宽度。⌘Z 撤销。',
    target: at('canvas'),
    placement: 'top',
  },
  {
    title: '细调在右边',
    description: '选中任何区块，字号、对齐、数据来源、合计行都在这里；选中格子还会多出「单元格」样式。',
    target: at('props'),
    placement: 'left',
  },
  {
    title: '切「预览」看真实数据',
    description: '编辑态显示的是结构（字段是胶囊），预览态填的是当前记录的真值。确认没问题就完成，回侧边栏打印。',
    target: at('mode'),
    placement: 'bottom',
  },
];

export function useEditorTour(): { open: boolean; show: () => void; close: () => void } {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(SEEN_KEY)) {
        localStorage.setItem(SEEN_KEY, '1');
        // 等三栏都挂上再指，否则目标元素还不在
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      /* 无痕模式读不到存储：不自动弹 */
    }
    return undefined;
  }, []);
  return { open, show: () => setOpen(true), close: () => setOpen(false) };
}

export function EditorTour({ open, onClose }: { open: boolean; onClose: () => void }) {
  return <Tour open={open} onClose={onClose} steps={STEPS} gap={{ offset: 6, radius: 8 }} />;
}
